"use client";

import { c as _c } from "react/compiler-runtime";
import { CalendarDays } from "lucide-react";
import { usePathname } from "next/navigation";
import { useMessagingUnreadCount } from "~/components/messaging/useUnreadCount";
import { Button } from "~/components/ui/button";
import { useNavAuthStore } from "~/store/navAuthStore";
import { useGuardedNavigate } from "~/store/navGuardStore";
import { MobileMenuTrigger } from "./MobileMenuTrigger";
import { SignedOutActions } from "./SignedOutActions";
import { UserMenu } from "./UserMenu";
import { type NavProfile, useNavContext, useNavProfile } from "./useNavAuth";

/**
 * The auth-dependent right-hand side of the navbar. Everything here waits on
 * `/auth/me`; anything that doesn't belongs in the shell instead.
 *
 * Below `md` this is also where the menu trigger comes from, because it is the
 * name pill when signed in and a bare icon when not.
 */
export function NavBarActionsClient(t0) {
  const $ = _c(31);
  const {
    profile: serverProfile,
    initialContext,
    driverFeaturesEnabled
  } = t0;
  const pathname = usePathname();
  const profile = useNavProfile(serverProfile);
  let t1;
  if ($[0] !== initialContext || $[1] !== profile) {
    t1 = {
      profile,
      initialContext,
      persist: true
    };
    $[0] = initialContext;
    $[1] = profile;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentContext = useNavContext(t1);
  const guardedNavigate = useGuardedNavigate();
  const clientAuthenticated = useNavAuthStore(_temp);
  const unreadMessages = useMessagingUnreadCount(clientAuthenticated === true);
  if (!profile) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <SignedOutActions />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== pathname) {
    t2 = pathname.startsWith("/search");
    $[4] = pathname;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isSearchPage = t2;
  let t3;
  if ($[6] !== currentContext || $[7] !== driverFeaturesEnabled || $[8] !== profile.isHost) {
    t3 = currentContext === "host" ? driverFeaturesEnabled ? {
      href: "/parking",
      label: "Find a Spot"
    } : null : profile.isHost ? {
      href: "/host/listings",
      label: "Switch to Host"
    } : {
      href: "/host/onboarding",
      label: "Become a Host"
    };
    $[6] = currentContext;
    $[7] = driverFeaturesEnabled;
    $[8] = profile.isHost;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const cta = t3;
  let t4;
  if ($[10] !== driverFeaturesEnabled || $[11] !== isSearchPage) {
    t4 = isSearchPage && driverFeaturesEnabled ? [{
      label: "Reservations",
      href: "/driver/bookings",
      icon: <CalendarDays className="h-4 w-4" strokeWidth={2} />
    }] : undefined;
    $[10] = driverFeaturesEnabled;
    $[11] = isSearchPage;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const searchPageExtraItems = t4;
  let t5;
  if ($[13] !== cta || $[14] !== guardedNavigate || $[15] !== isSearchPage) {
    t5 = !isSearchPage && cta && <Button variant="ghost" size="sm" onClick={() => guardedNavigate(cta.href)}>{cta.label}</Button>;
    $[13] = cta;
    $[14] = guardedNavigate;
    $[15] = isSearchPage;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== driverFeaturesEnabled || $[18] !== profile || $[19] !== searchPageExtraItems || $[20] !== unreadMessages) {
    t6 = <UserMenu {...profile} unreadMessages={unreadMessages} driverFeaturesEnabled={driverFeaturesEnabled} extraItems={searchPageExtraItems} />;
    $[17] = driverFeaturesEnabled;
    $[18] = profile;
    $[19] = searchPageExtraItems;
    $[20] = unreadMessages;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <div className="hidden items-center gap-2 md:flex">{t5}{t6}</div>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== profile.firstName || $[26] !== unreadMessages) {
    t8 = <MobileMenuTrigger name={profile.firstName} unreadMessages={unreadMessages} />;
    $[25] = profile.firstName;
    $[26] = unreadMessages;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t7 || $[29] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function _temp(s) {
  return s.clientAuthenticated;
}